// BLE lamp node panel — SHARED by both shells (moved from ui/mobile/screens/
// device/BleNodeScreen.jsx 2026-10-11).
//
//   Phone:   ui/mobile/screens/device/BleNodeScreen.jsx = its DeviceAppBar + this.
//   Desktop: ui/desktop/pages/device/BleNodePage.jsx    = DeviceShell + this, with
//            remoteOnly: the desktop has no Bluetooth, so a Telink (mesh) group is
//            controlled through the location's HUB (docs/20), and everything that
//            needs a phone near the lamps — pairing, removing, configuring,
//            reversing, moving a group between parents — is not offered there.
//
// BLE lamp node (DeviceID 100) — a PARENT or a GROUP; the control screen.
//
//   Parent  "All WWH Lights"        controls → every lamp under every child group
//           children: groups        (tap one to open it; On/Off/Blink in place)
//   Group   "Left Section WWH"      controls → its lamps
//           children: lamps         (each with its own sliders, rename, blink…)
//
// Same two vertical sliders and presets as the Wi-Fi white/warm light
// (LightScreen 'wwh'), because to the user it IS a white/warm light. A command
// from any level is the same packet to each lamp beneath in turn, ~HoldMs per
// lamp, with a "Sending 7/25 wwh7…" line while it runs.
//
// Layout follows the lamp count on a group: 0 lamps → how to add one; 1 lamp
// → ONE control plus the lamp's compact row (two identical slider pairs for
// one fixture was confusing); 2+ → Group / Individual tabs.
//
// Nothing here goes over MQTT; it only works in the Android app near the
// lamps, and nothing reports back — the sliders show the LAST VALUES SENT.
//
// ⚠️ THE LAMPS HAVE NO SECURITY. Lock = the server withholds the radio
// identity from everyone but the owner. See docs/14-ble-lamps.md.
//
// BLUETOOTH MESH NODES (Codec 'sig_mesh', Telink white/warm/cool lamps) use
// the same screen with three differences: the sliders are Brightness and
// Warm↔Cool (meshValues.js), a command is ONE message to the node's group
// address instead of a loop, and "Add a lamp" scans for lamps waiting to be
// paired instead of holding a pairing packet. Lamps answer, so a single-lamp
// command that fails says so.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Checkbox, IconButton, LinearProgress, Menu, MenuItem, ListItemIcon, ListItemText, Switch, Tab, Tabs, TextField,
  Tooltip, Typography, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/Delete';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { PresetButton, VerticalSlider } from '../controls/LightBits';
import { useMessage } from '../MessageDialog';
import { useApp } from '../../../core/state/AppContext';
import { useBleNode } from '../../../core/hooks/useBleNode';
import { WWH_PRESETS } from '../../../core/devices/light';
import { codecsForApp, LAMP_SMART, FAN_LAMP } from '../../../core/ble/lamp/codecs';
import { MESH_PRESETS, isMeshCodec } from '../../../core/ble/mesh/meshValues';
import { countOn, isSwapped, lastUsedText } from '../../../core/ble/lampState';

import { getRooms } from '../../../core/api/api';
import { getBleNodesAtLocation } from '../../../core/api/bleNodes';
import { isNativePlatform } from '../../../core/platform';
import { offlineSinceText } from '../../../core/ble/mesh/hubStatus';
import OrphanBleRemove from './OrphanBleRemove';

/** "70% · warm" / "W 200 · WW 40" — what a lit lamp is showing. */
function describeValues(st, mesh) {
  if (mesh) {
    // The same names as the preset buttons (meshValues MESH_PRESETS): warmest =
    // Warm White, middle = Cool White, coolest = White — the customer's naming.
    let tone = 'cool white';
    if (st.b < 85) tone = 'warm white';
    else if (st.b > 170) tone = 'white';
    return `${Math.round((st.a / 255) * 100)}% · ${tone}`;
  }
  return `W ${st.a} · WW ${st.b}`;
}

// ── small pieces ─────────────────────────────────────────────────────────────

// LampSmart: White / Warm White channels. Mesh: Brightness / Warm↔Cool.
const CHANNEL_LABELS = {
  wwh: [{ label: 'White', color: '#FFFFFF' }, { label: 'Warm White', color: '#FFD08A' }],
  mesh: [{ label: 'Brightness', color: '#FFFFFF' }, { label: 'Warm ↔ Cool', color: '#CFE3FF' }],
};

function ChannelPair({
  values, onChange, onCommit, disabled, height = 200, mesh = false,
}) {
  const [a, b] = CHANNEL_LABELS[mesh ? 'mesh' : 'wwh'];
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <VerticalSlider label={a.label} height={height} value={values[0] || 0} activeColor={a.color} onChange={(v) => onChange([v, values[1] || 0])} onCommit={onCommit} />
      <VerticalSlider label={b.label} height={height} value={values[1] || 0} activeColor={b.color} onChange={(v) => onChange([values[0] || 0, v])} onCommit={onCommit} />
    </Box>
  );
}

function Presets({
  onPreset, disabled, size = 70, mesh = false,
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: size >= 70 ? 5 : 3, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      {(mesh ? MESH_PRESETS : WWH_PRESETS).map((p) => (
        <PresetButton key={p.label} label={p.label} swatch={p.swatch} size={size} onClick={() => onPreset(p.command)} />
      ))}
    </Box>
  );
}

function RadioNotice({ diagnostics, c }) {
  if (!diagnostics) return null;
  let text = '';
  if (!diagnostics.supported || !diagnostics.plugin || diagnostics.radioReady === false) text = diagnostics.detail;
  else if (diagnostics.permission === 'denied') text = 'The "Nearby devices" permission is refused. Allow it in Settings › Apps › Smart Life India › Permissions.';
  if (!text) return null;
  return (
    <Box sx={{ mx: 2, mt: 1, p: 1.5, borderRadius: '12px', bgcolor: '#b3261e22', border: '1px solid #b3261e' }}>
      <Typography sx={{ color: c.text, fontSize: 14 }}>{text}</Typography>
    </Box>
  );
}

const OutlinedButton = ({ c, children, ...rest }) => (
  <Button variant="outlined" sx={{ color: c.text, borderColor: `${c.text}66` }} {...rest}>{children}</Button>
);

/** The sliders + presets + On/Off/Night/Blink block, for the node or one lamp. */
function ControlBlock({
  c, values, setValues, ctl, lamp, size = 70, height = 200,
}) {
  // The sliders move with every button. Success: the new LastValues moves them
  // (lampState.sliderValues — Off = brightness 0). Failure: back to what the
  // lamp last had, never left showing a value that was not sent.
  const stored = () => (lamp ? ctl.lampValues(lamp) : ctl.nodeValues);
  const revertIfFailed = (ok) => { if (ok === false) setValues(stored()); };
  const commit = () => ctl.setValues(values[0], values[1], lamp).then(revertIfFailed);
  const preset = (cmd) => {
    const [w, ww] = cmd.split(',').map(Number);
    // Mesh Off keeps the lamp's colour (only brightness goes to 0).
    const next = ctl.isMesh && w === 0 ? [0, values[1] || 0] : [w, ww];
    setValues(next);
    ctl.setValues(next[0], next[1], lamp).then(revertIfFailed);
  };
  return (
    <>
      <ChannelPair values={values} onChange={setValues} onCommit={commit} disabled={ctl.busy} height={height} mesh={ctl.isMesh} />
      <Box sx={{ height: size >= 70 ? 20 : 12 }} />
      <Presets onPreset={preset} disabled={ctl.busy} size={size} mesh={ctl.isMesh} />
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <OutlinedButton c={c} disabled={ctl.busy} onClick={() => ctl.turnOn(lamp)}>On</OutlinedButton>
        <OutlinedButton c={c} disabled={ctl.busy} onClick={() => ctl.turnOff(lamp)}>Off</OutlinedButton>
        <OutlinedButton c={c} disabled={ctl.busy} onClick={() => ctl.nightMode(lamp)}>Night</OutlinedButton>
        <OutlinedButton c={c} disabled={ctl.busy} onClick={() => ctl.blink(lamp)} startIcon={<FlashOnIcon />}>Blink</OutlinedButton>
      </Box>
    </>
  );
}

// ── one lamp row (in a group) ────────────────────────────────────────────────

function LampRow({
  lamp, ctl, rooms, isOwner, onRename, onMove, onRemove, onRePair, onReverse, compact = false, remoteOnly = false,
  dense = false, // the desktop room card: same row, card-sized
}) {
  const theme = useTheme();
  const c = theme.sli;
  const [open, setOpen] = useState(false);
  const [menuEl, setMenuEl] = useState(null);
  const [values, setValues] = useState(ctl.lampValues(lamp));
  useEffect(() => { setValues(ctl.lampValues(lamp)); }, [lamp.LastValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = Number(lamp.CanSend) !== 1;
  const room = rooms.find((r) => String(r.RoomID) === String(lamp.RoomID));
  const st = ctl.lampState(lamp); // { a, b, on } — null when never used

  return (
    <Box sx={{ bgcolor: c.button, borderRadius: dense ? '12px' : '16px', mb: dense ? 0.75 : 1.5, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: dense ? 0.5 : 1.5, py: dense ? 0.25 : 1 }}>
        {!compact && (
          <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ color: c.text, p: dense ? 0.25 : undefined }}>
            {open ? <ExpandLessIcon fontSize={dense ? 'small' : 'medium'} /> : <ExpandMoreIcon fontSize={dense ? 'small' : 'medium'} />}
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, ml: 0.5, cursor: compact ? 'default' : 'pointer' }} onClick={() => !compact && setOpen((v) => !v)}>
          <Typography sx={{ color: c.text, fontWeight: 600, fontSize: dense ? 13.5 : 16, lineHeight: dense ? 1.25 : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lamp.Name}
          </Typography>
          <Typography noWrap sx={{ color: `${c.text}99`, fontSize: dense ? 11 : 12, lineHeight: dense ? 1.25 : undefined }}>
            {/* The card is already the room: the dense row leaves the room name out. */}
            {dense ? '' : (room?.RoomName || 'Room not set')}
            {ctl.isMesh && Number(lamp.MeshConfigured) !== 1 && Number(lamp.CanSend) === 1 && ' · not configured'}
            {isSwapped(lamp) && ' · reversed'}
            {(() => {
              const sep = dense ? '' : ' · ';
              if (!st) return `${sep}not used yet`;
              return st.on ? `${sep}${describeValues(st, ctl.isMesh)}` : (dense ? 'off' : '');
            })()}
          </Typography>
          {(() => {
            const used = lastUsedText(lamp);
            if (!used) return null;
            return (
              <Typography noWrap sx={{ color: `${c.text}80`, fontSize: dense ? 10 : 11, lineHeight: dense ? 1.2 : undefined }}>
                {dense ? used : `Last used ${used}`}
              </Typography>
            );
          })()}
        </Box>
        {Number(lamp.Locked) === 1 && <LockIcon fontSize="small" sx={{ color: c.text, mr: 1 }} />}
        <IconButton size="small" disabled={ctl.busy || locked} onClick={() => ctl.blink(lamp)} sx={{ color: c.text, p: dense ? 0.25 : undefined }} title="Blink this lamp">
          <FlashOnIcon fontSize={dense ? 'small' : 'medium'} />
        </IconButton>
        {/* The lamp's state, and ONE switch that flips it — not two buttons that say nothing. */}
        <Typography sx={{
          color: st?.on ? '#FFD54F' : `${c.text}99`, fontWeight: 800, fontSize: dense ? 11 : 13, minWidth: dense ? 24 : 30, textAlign: 'right',
        }}
        >
          {st ? (st.on ? 'ON' : 'OFF') : '—'}
        </Typography>
        <Switch
          size="small"
          checked={!!st?.on}
          disabled={ctl.busy || locked}
          onChange={(e) => (e.target.checked ? ctl.turnOn(lamp) : ctl.turnOff(lamp))}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD54F' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#FFD54F' } }}
        />
        <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)} sx={{ color: c.text, p: dense ? 0.25 : undefined }}>
          <MoreVertIcon fontSize={dense ? 'small' : 'medium'} />
        </IconButton>
      </Box>

      {open && !compact && (
        <Box sx={{ px: dense ? 1 : 2, pb: dense ? 1.25 : 2, pt: dense ? 0.5 : 1 }}>
          {locked
            ? <Typography sx={{ color: c.text, fontSize: 14, textAlign: 'center', py: 2 }}>This lamp is locked by its owner.</Typography>
            : <ControlBlock c={c} values={values} setValues={setValues} ctl={ctl} lamp={lamp} size={dense ? 40 : 48} height={dense ? 110 : 150} />}
        </Box>
      )}

      <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
        {ctl.isMesh && lamp.UniqueNumber && (
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <ListItemText
              primary={lamp.UniqueNumber}
              secondary={lamp.MeshMac ? `MAC ${lamp.MeshMac}` : 'Chip id'}
              primaryTypographyProps={{ fontSize: 11, fontFamily: 'monospace' }}
              secondaryTypographyProps={{ fontSize: 11, fontFamily: 'monospace' }}
            />
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMenuEl(null); ctl.blink(lamp); }} disabled={locked || ctl.busy}>
          <ListItemIcon><FlashOnIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Blink to identify</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuEl(null); onRename(lamp); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuEl(null); onMove(lamp); }}>
          <ListItemIcon><MeetingRoomIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Move to room</ListItemText>
        </MenuItem>
        {/* Bluetooth pairing / configuring needs a phone next to the lamp: on the
            desktop (remoteOnly) it is SHOWN, greyed out, and says so. */}
        {ctl.isMesh ? (
          <MenuItem onClick={() => { setMenuEl(null); ctl.reconfigure(lamp); }} disabled={(remoteOnly && !ctl.hubUno) || locked || ctl.busy || !lamp.MeshDevKey}>
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={lamp.MeshDevKey ? 'Configure again' : 'Configure again (owner only)'}
              secondary={remoteOnly && !ctl.hubUno ? 'Phone app, near the lamps' : undefined}
            />
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { setMenuEl(null); onRePair(lamp); }} disabled={remoteOnly || locked}>
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Pair again" secondary={remoteOnly ? 'Phone app, near the lamps' : undefined} />
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem onClick={() => { setMenuEl(null); onReverse(lamp); }} disabled={ctl.busy}>
            <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={isSwapped(lamp) ? 'Reversed white / warm ✓' : 'Reverse white / warm white'}
              secondary={isSwapped(lamp) ? 'Tap to set back to normal' : 'If White shows warm on this lamp'}
            />
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem onClick={() => { setMenuEl(null); ctl.setLock(lamp, Number(lamp.Locked) !== 1); }}>
            <ListItemIcon>{Number(lamp.Locked) === 1 ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{Number(lamp.Locked) === 1 ? 'Unlock' : 'Lock'}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMenuEl(null); onRemove(lamp); }} disabled={locked}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Remove from group</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

// ── one child group row (in a parent) ────────────────────────────────────────

function GroupRow({
  group, ctl, isOwner, onOpen, onRename, onDetach, onRemove, remoteOnly = false,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const [menuEl, setMenuEl] = useState(null);
  const locked = Number(group.CanSend) !== 1;
  // Commands to a child group from here = its lamps only.
  const lampsOf = ctl.lamps.filter((l) => l.ParentNodeId === group.NodeId);

  return (
    <Box sx={{ bgcolor: c.button, borderRadius: '16px', mb: 1.5, display: 'flex', alignItems: 'center', px: 1.5, py: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => onOpen(group)}>
        <Typography sx={{ color: c.text, fontWeight: 600, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {group.Name}
        </Typography>
        <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>
          {group.LampCount ?? lampsOf.length}
          {' lamps'}
          {group.NodeType === 'parent' ? ' · parent' : ''}
          {Number(group.Locked) === 1 ? ' · locked' : ''}
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => onOpen(group)} sx={{ color: c.text }}><ChevronRightIcon /></IconButton>
      <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)} sx={{ color: c.text }}><MoreVertIcon /></IconButton>
      <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
        <MenuItem onClick={() => { setMenuEl(null); onOpen(group); }}>
          <ListItemIcon><ChevronRightIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuEl(null); onRename(group); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        {isOwner && (
          <MenuItem onClick={() => { setMenuEl(null); ctl.setLock(group, Number(group.Locked) !== 1); }}>
            <ListItemIcon>{Number(group.Locked) === 1 ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{Number(group.Locked) === 1 ? 'Unlock' : 'Lock'}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMenuEl(null); onDetach(group); }} disabled={locked}>
          <ListItemIcon><LinkOffIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Take out of this parent</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuEl(null); onRemove(group); }} disabled={locked || lampsOf.length > 0}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{lampsOf.length > 0 ? 'Delete (remove its lamps first)' : 'Delete group'}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

// ── the screen ────────────────────────────────────────────────────────────────

/**
 * renderHeader(ctl | null) — the shell's own header (the phone's DeviceAppBar);
 * null while loading. remoteOnly — the desktop: no Bluetooth of its own.
 * view 'individual' — only the Lamps header (Lock · ⟳ · Settings) and the
 * phone's lamp rows, one per lamp however many there are: the desktop room
 * card's Individual tab.
 */
export default function BleNodePanel({
  device, renderHeader = () => null, remoteOnly = false, view = 'full',
}) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId } = useApp();
  const { show } = useMessage();
  const ctl = useBleNode(device?.UniqueNumber, userId);
  const { node, children, lamps } = ctl;
  const isParent = ctl.isParent;

  const [values, setValues] = useState([0, 0]);
  useEffect(() => { if (node) setValues(ctl.nodeValues); }, [node?.LastValues]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tabState, setTab] = useState('group');
  const individualOnly = view === 'individual';
  const dense = individualOnly; // the desktop room card
  const tab = individualOnly ? 'individual' : tabState;

  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    const locationId = node?.LocationID ?? device?.LocationID;
    if (!userId || !locationId) return undefined;
    let alive = true;
    getRooms(userId, locationId)
      .then((res) => { if (alive) setRooms(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [userId, node?.LocationID, device?.LocationID]);

  const [dialog, setDialog] = useState(null); // { kind, target? }
  const [lampsMenu, setLampsMenu] = useState(null); // ⋮ on the Lamps header
  const [text, setText] = useState('');
  const [pickRoom, setPickRoom] = useState('');
  const [pickNode, setPickNode] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [holdText, setHoldText] = useState('');
  const [reversedSel, setReversedSel] = useState([]); // NodeIds ticked as reversed
  // "Last used 40 s ago" must keep counting while the screen is open.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 10000);
    return () => clearInterval(t);
  }, []);

  /** One lamp from its ⋮ menu: flip its reversed flag (stand near it). */
  const reverseOne = async (lamp) => {
    const res = await ctl.setLampReversed(lamp, !isSwapped(lamp));
    if (res && res.notUpdated.length) {
      show('Saved — lamp not updated', `${lamp.Name} could not be updated over Bluetooth. Stand near it and use "Configure again" from its menu.`);
    } else if (res) {
      show(isSwapped(lamp) ? 'Back to normal' : 'Reversed', `${lamp.Name} now gets the ${isSwapped(lamp) ? 'normal' : 'opposite'} colour, so White shows white.`);
    }
  };
  // Mesh "Add a lamp": lamps found waiting to be paired, and the one picked.
  const [found, setFound] = useState({});
  const [picked, setPicked] = useState([]); // deviceIds ticked for pairing
  const stopScanRef = useRef(null);

  const startMeshScan = async () => {
    setFound({});
    setPicked([]);
    try {
      stopScanRef.current = await ctl.scanForMeshLamps((d) => setFound((prev) => {
        if (d.error) { show('Could not scan', d.error); return prev; }
        const old = prev[d.deviceId];
        // Adverts arrive many times a second; redraw only on news.
        if (old && Math.abs(old.rssi - d.rssi) < 6 && d.lastSeen - old.lastSeen < 2000) return prev;
        return { ...prev, [d.deviceId]: d };
      }));
    } catch (e) {
      show('Could not scan', e?.message || String(e));
    }
  };
  const stopMeshScan = () => {
    const stop = stopScanRef.current;
    stopScanRef.current = null;
    if (stop) stop();
  };
  useEffect(() => stopMeshScan, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = async (kind, target = null) => {
    setText(kind === 'rename' ? target.Name : '');
    setPickRoom(String(target?.RoomID ?? node?.RoomID ?? ''));
    setHoldText(String(node?.HoldMs ?? 300));
    if (kind === 'add' && ctl.isMesh) startMeshScan();
    if (kind === 'adopt') {
      try {
        const res = await getBleNodesAtLocation(userId, node.LocationID, 'group');
        // Only groups of the same radio family can go under this parent.
        const mine = (res.nodes || []).filter((g) => g.ParentNodeId !== node.NodeId && g.NodeId !== node.NodeId
          && isMeshCodec(g.Codec) === ctl.isMesh);
        setCandidates(mine);
        setPickNode(mine[0] ? String(mine[0].NodeId) : '');
      } catch (e) {
        show('Could not list groups', e.message);
        return;
      }
    }
    setDialog({ kind, target });
  };
  const closeDialog = () => {
    stopMeshScan();
    setDialog(null);
  };

  const lastStatus = React.useRef('');
  useEffect(() => {
    if (ctl.failed && ctl.status && ctl.status !== lastStatus.current) show('That did not work', ctl.status);
    lastStatus.current = ctl.status;
  }, [ctl.failed, ctl.status, show]);

  const openNode = (n) => navigate(`/device/${n.UniqueNumber}`, { state: { locationId: node.LocationID, roomId: n.RoomID } });

  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submitAddMesh = async () => {
    const devices = picked.map((id) => found[id]).filter(Boolean);
    if (!devices.length) return;
    stopMeshScan();
    const res = await ctl.addMeshLamps(node.NodeId, devices, text.trim(), pickRoom ? Number(pickRoom) : undefined);
    closeDialog();
    if (!res) return;
    const lines = [];
    res.added.forEach(({ lamp, movedFrom: m, configureError }) => {
      let note = '';
      if (m && m.SameGroup) note = ' (renewed — it was already here)';
      else if (m && m.GroupName) note = ` (moved from "${m.GroupName}")`;
      else if (m) note = ' (moved from another group)';
      if (configureError) note += ' — paired, but configure failed: use "Configure again"';
      lines.push(`✓ ${lamp.Name}${note}`);
    });
    res.failed.forEach(({ device, error }) => lines.push(`✗ ${device.deviceId}: ${error}`));
    show(res.added.length === 1 && !res.failed.length ? 'Lamp added' : `${res.added.length} of ${res.added.length + res.failed.length} lamps added`, lines.join('\n'));
  };

  const submitRemoveAll = async () => {
    closeDialog();
    const res = await ctl.removeAllLamps();
    if (!res) return;
    show(
      `${res.removed} lamp(s) removed`,
      res.notReset.length
        ? `These did not answer and still hold this location's keys — reset them at the wall (power-cycle 3–5 times): ${res.notReset.join(', ')}. They are out of the group either way.`
        : 'Every lamp was factory-reset and is ready to be paired again, into any group.',
    );
  };

  // Individual view (or a one-lamp group): ask the lamps what they are ACTUALLY
  // doing, so a lamp switched at the wall shows right. Background — silent.
  const showingLamps = !isParent && (tab === 'individual' || lamps.length === 1);
  useEffect(() => {
    if (showingLamps && ctl.isMesh && lamps.length) ctl.readLampStates();
  }, [showingLamps, ctl.isMesh, lamps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitAdd = async () => {
    const name = text.trim();
    const ok = await ctl.addLamp(node.NodeId, name, pickRoom ? Number(pickRoom) : undefined);
    closeDialog();
    if (ok) show('Pairing sent', 'The lamp blinks when it accepts. If it did not, power-cycle only that lamp and use "Pair again" from its menu.');
  };

  const codecs = useMemo(() => [...codecsForApp(LAMP_SMART), ...codecsForApp(FAN_LAMP)], []);
  const locked = !ctl.canSendAny && lamps.length > 0;
  const groupLamps = children.filter((x) => x.NodeType === 'lamp');
  const childGroups = children.filter((x) => x.NodeType !== 'lamp');
  const single = !isParent && lamps.length === 1;

  if (ctl.loading || !node) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
        {renderHeader(null)}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          {ctl.error ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: c.text }}>{ctl.error}</Typography>
              {ctl.orphan && (
                <Box sx={{ mt: 2 }}><OrphanBleRemove device={device} size="medium" onRemoved={() => navigate(-1)} /></Box>
              )}
            </Box>
          ) : <CircularProgress sx={{ color: c.text }} />}
        </Box>
      </Box>
    );
  }

  // The hub must be LIVE (answering MESHPING) and hold this location's keys.
  // Desktop: without it nothing here reaches a lamp, so everything below the
  // notice is greyed. Phone: Bluetooth near the lamps still works — it only says so.
  const hubDown = ctl.isMesh && !!ctl.hubUno && ctl.hubLive === false;
  const noKeys = ctl.isMesh && !!ctl.hubUno && ctl.hubLive === true && ctl.hubReady === false;
  const since = offlineSinceText(ctl.hubLastLiveAt);
  const greyed = remoteOnly && !individualOnly && (hubDown || noKeys); // the card greys itself
  const hubProblem = () => {
    if (hubDown) {
      return remoteOnly
        ? `⚠️ The hub (${ctl.hubUno}) is offline${since ? ` ${since}` : ''}. These lamps cannot be reached from here until it is back — or use the phone app, near them.`
        : `⚠️ The hub (${ctl.hubUno}) is offline${since ? ` ${since}` : ''} — these lamps answer only to this phone’s Bluetooth, near them.`;
    }
    if (noKeys) return `⚠️ The hub (${ctl.hubUno}) is online but has not been given this location’s mesh keys.`;
    return null;
  };

  const controlTitle = () => {
    if (isParent) return `All lamps (${lamps.length})`;
    if (single) return lamps[0].Name;
    return `All lamps (${lamps.length})`;
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      {renderHeader(ctl)}
      {ctl.busy && <LinearProgress sx={{ height: 3 }} />}

      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        pb: dense ? 1 : 3,
        ...(greyed && {
          '& > :not(.hub-notice)': { opacity: 0.45, pointerEvents: 'none', filter: 'grayscale(1)' },
        }),
      }}
      >
        {!individualOnly && (hubDown || noKeys) && (
          <Box className="hub-notice" sx={{ mx: 2, mt: 1, p: 1.5, borderRadius: '12px', bgcolor: 'rgba(239,83,80,0.12)', border: '1px solid rgba(239,83,80,0.5)' }}>
            <Typography sx={{ color: c.text, fontSize: 14 }}>{hubProblem()}</Typography>
          </Box>
        )}
        {!individualOnly && !(hubDown || noKeys) && (remoteOnly || !isNativePlatform()) && (
          <Box sx={{ mx: 2, mt: 1, p: 1.5, borderRadius: '12px', bgcolor: c.button }}>
            <Typography sx={{ color: c.text, fontSize: 14 }}>
              {(() => {
                if (!ctl.isMesh) return 'BLE lamps are controlled by the phone’s Bluetooth. Open this device in the Android app, near the lamps.';
                if (ctl.hubUno === undefined) return 'Looking for this location’s hub…';
                if (!ctl.hubUno) return 'No hub at this location — control these lamps from the phone app, near them.';
                return `🌐 Controlled through this location’s hub (${ctl.hubUno}) — Wi-Fi to Bluetooth. Adding, configuring and removing lamps go through the hub too.`;
              })()}
            </Typography>
          </Box>
        )}
        {!remoteOnly && <RadioNotice diagnostics={ctl.diagnostics} c={c} />}

        {/* Where this node sits in the tree. */}
        {ctl.ancestors.length > 0 && (
          <Box sx={{ mx: 2, mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>Part of</Typography>
            {ctl.ancestors.map((a) => (
              <Chip key={a.NodeId} size="small" label={a.Name} onClick={() => openNode(a)} sx={{ color: c.text, bgcolor: c.button }} />
            ))}
          </Box>
        )}

        {!isParent && lamps.length >= 2 && !individualOnly && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ mx: 2, mt: 1, '& .MuiTab-root': { color: `${c.text}99`, fontWeight: 700 }, '& .Mui-selected': { color: `${c.text} !important` } }}>
            <Tab value="group" label={`Group (${lamps.length})`} />
            <Tab value="individual" label="Individual" />
          </Tabs>
        )}

        {/* ── Controls for everything under this node ─────────────────── */}
        {lamps.length >= 1 && !individualOnly && (isParent || single || tab === 'group') && (!remoteOnly || (ctl.isMesh && ctl.hubUno)) && (
          <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 18 }}>{controlTitle()}</Typography>
                {lamps.length >= 2 && (() => {
                  const k = countOn(lamps, { mesh: ctl.isMesh });
                  const used = lastUsedText(node);
                  return <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>{`${k.on} of ${k.total} on${used ? ` · last used ${used}` : ''}`}</Typography>;
                })()}
              </Box>
              {Number(node.Locked) === 1 && <Chip size="small" icon={<LockIcon />} label="Locked" sx={{ color: c.text }} />}
            </Box>
            {lamps.length >= 2 && (
              <Typography sx={{ color: `${c.text}99`, fontSize: 12, mb: 1.5 }}>
                {ctl.isMesh
                  ? `One message to all ${lamps.length} lamps — the mesh passes it on.`
                  : `Sent to each lamp in turn — about ${((lamps.length * (Number(node.HoldMs) || 300)) / 1000).toFixed(1)} s.`}
              </Typography>
            )}
            {ctl.isMesh && ctl.via && (
              <Typography sx={{ color: ctl.via === 'hub' ? '#FFD54F' : `${c.text}99`, fontSize: 12, mb: 1 }}>
                {ctl.via === 'hub'
                  ? '🌐 Away from the lamps — sent through the hub (Wi-Fi → Bluetooth).'
                  : '📶 Near the lamps — sent over this phone’s Bluetooth.'}
              </Typography>
            )}
            {ctl.progress && (
              <Typography sx={{ color: c.text, fontSize: 13, mb: 1 }}>
                {`${ctl.progress.verb || 'Sending'} ${ctl.progress.i}/${ctl.progress.n} — ${ctl.progress.name}…`}
              </Typography>
            )}
            {locked
              ? <Typography sx={{ color: c.text, textAlign: 'center', py: 3 }}>Locked by the owner.</Typography>
              : <ControlBlock c={c} values={values} setValues={setValues} ctl={ctl} />}
          </Box>
        )}

        {/* ── Members ─────────────────────────────────────────────────── */}
        <Box sx={dense ? { px: 1, pt: 1 } : { maxWidth: 480, mx: 'auto', px: 2, pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: dense ? 0.5 : 1 }}>
            <Typography sx={{ color: c.text, fontWeight: 700, fontSize: dense ? 14 : 18 }}>
              {isParent ? `Groups (${childGroups.length})` : (single ? 'This lamp' : `Lamps${dense ? ` (${lamps.length})` : ''}`)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {ctl.isOwner && dense && (
                <Tooltip title={Number(node.Locked) === 1 ? 'Unlock' : 'Lock'}>
                  <IconButton size="small" onClick={() => ctl.setLock(node, Number(node.Locked) !== 1)} sx={{ color: c.text }}>
                    {Number(node.Locked) === 1 ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              {ctl.isOwner && !dense && (
                <Button size="small" onClick={() => ctl.setLock(node, Number(node.Locked) !== 1)} sx={{ color: c.text }} startIcon={Number(node.Locked) === 1 ? <LockOpenIcon /> : <LockIcon />}>
                  {Number(node.Locked) === 1 ? 'Unlock' : 'Lock'}
                </Button>
              )}
              {ctl.isMesh && showingLamps && lamps.length > 0 && (
                <IconButton size="small" disabled={ctl.busy || ctl.reading} onClick={() => ctl.readLampStates()} sx={{ color: c.text }} title="Read the lamps' real state">
                  {ctl.reading ? <CircularProgress size={dense ? 16 : 18} sx={{ color: c.text }} /> : <RefreshIcon fontSize={dense ? 'small' : 'medium'} />}
                </IconButton>
              )}
              {dense ? (
                <Tooltip title="Settings">
                  <IconButton size="small" onClick={() => openDialog('settings')} sx={{ color: c.text }}><SettingsIcon fontSize="small" /></IconButton>
                </Tooltip>
              ) : (
                <Button size="small" onClick={() => openDialog('settings')} sx={{ color: c.text }}>Settings</Button>
              )}
              {/* Destructive, so behind ⋮ — not a button beside "Add lamp". */}
              {ctl.isOwner && !isParent && groupLamps.length > 0 && (
                <>
                  <IconButton size="small" onClick={(e) => setLampsMenu(e.currentTarget)} sx={{ color: c.text }} aria-label="More">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu anchorEl={lampsMenu} open={!!lampsMenu} onClose={() => setLampsMenu(null)}>
                    <MenuItem disabled={ctl.busy} onClick={() => { setLampsMenu(null); openDialog('removeAll'); }}>
                      <ListItemIcon><DeleteSweepIcon fontSize="small" color="error" /></ListItemIcon>
                      <ListItemText>{`Remove all lamps (${groupLamps.length})`}</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          {isParent ? (
            <>
              {childGroups.length === 0 && (
                <Typography sx={{ color: `${c.text}99`, fontSize: 14, mb: 2 }}>
                  No groups yet. Create one here, or bring an existing group into this parent.
                </Typography>
              )}
              {childGroups.map((g) => (
                <GroupRow
                  key={g.NodeId}
                  group={g}
                  ctl={ctl}
                  isOwner={ctl.isOwner}
                  onOpen={openNode}
                  onRename={(t) => openDialog('rename', t)}
                  onDetach={(t) => openDialog('detach', t)}
                  onRemove={(t) => openDialog('removeGroup', t)}
                  remoteOnly={remoteOnly}
                />
              ))}
              {(
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={ctl.busy || remoteOnly}
                  title={remoteOnly ? 'Phone app' : undefined}
                  onClick={() => navigate(`/ble-lamp-group/new?locationId=${node.LocationID}&roomId=${node.RoomID}&parentNodeId=${node.NodeId}`)}
                  sx={{ bgcolor: c.button, color: c.text, borderRadius: '12px', py: 1.5, '&:hover': { bgcolor: c.button } }}
                >
                  New group
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  disabled={ctl.busy}
                  onClick={() => openDialog('adopt')}
                  sx={{ color: c.text, borderColor: `${c.text}66`, borderRadius: '12px', py: 1.5 }}
                >
                  Add existing
                </Button>
              </Box>
              )}
            </>
          ) : (
            <>
              {groupLamps.length === 0 && (
                <Typography sx={{ color: `${c.text}99`, fontSize: 14, mb: 2 }}>
                  {ctl.isMesh
                    ? 'No lamps yet. Factory-reset a lamp so it waits to be paired, stand near it, and add it below.'
                    : 'No lamps yet. Add the first one below — you will need to switch it off and on at the wall.'}
                </Typography>
              )}
              {(single || tab === 'individual') && groupLamps.map((l) => (
                <LampRow
                  key={l.NodeId}
                  lamp={lamps.find((x) => x.NodeId === l.NodeId) || l}
                  ctl={ctl}
                  rooms={rooms}
                  isOwner={ctl.isOwner}
                  compact={single && !individualOnly}
                  dense={dense}
                  onRename={(t) => openDialog('rename', t)}
                  onMove={(t) => openDialog('move', t)}
                  onRemove={(t) => openDialog('remove', t)}
                  onRePair={(t) => openDialog('repair', t)}
                  onReverse={reverseOne}
                  remoteOnly={remoteOnly}
                />
              ))}
              {lamps.length >= 2 && tab === 'group' && (
                <Typography sx={{ color: `${c.text}99`, fontSize: 13, mb: 2 }}>
                  {groupLamps.map((l) => l.Name).join(' · ')}
                  {' — switch to Individual to control, rename or blink one lamp.'}
                </Typography>
              )}
              {/* Adding a lamp pairs it over Bluetooth: the phone's, or — on the
                  desktop — the HUB's (mesh lamps, hub firmware 1.1.0+). */}
              {(!remoteOnly || (ctl.isMesh && ctl.hubUno)) && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                disabled={ctl.busy || (locked && !ctl.isOwner)}
                onClick={() => openDialog('add')}
                sx={{
                  bgcolor: c.button, color: c.text, borderRadius: dense ? '10px' : '12px', py: dense ? 0.6 : 1.5, mt: dense ? 0.5 : 0, fontSize: dense ? 12 : undefined, '&:hover': { bgcolor: c.button },
                }}
              >
                {groupLamps.length === 0 ? 'Add the first lamp' : 'Add another lamp'}
              </Button>
              )}
            </>
          )}
        </Box>

        {!!ctl.status && (
          <Typography sx={{ color: ctl.failed ? '#ef5350' : `${c.text}99`, fontSize: 13, textAlign: 'center', mt: 2, px: 2 }}>{ctl.status}</Typography>
        )}
      </Box>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <Dialog open={!!dialog} onClose={ctl.busy ? undefined : closeDialog} PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 300 } }}>
        {dialog?.kind === 'add' && ctl.isMesh && (
          <>
            <DialogTitle sx={{ color: c.text }}>Add a lamp</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14, mb: 1.5 }}>
                {remoteOnly || !isNativePlatform()
                  ? 'Factory-reset the lamps (usually: switch each off and on at the wall 3–5 times until it blinks). The location’s hub listens for them and pairs them — they must be within its Bluetooth range. Tick every lamp to add; they are paired one after another.'
                  : 'Factory-reset the lamps (usually: switch each off and on at the wall 3–5 times until it blinks), stay near them, then tick every lamp to add — they are paired one after another. Each blinks while it is being paired.'}
              </Typography>
              {!ctl.busy && (
                <Box sx={{ mb: 2, maxHeight: 220, overflowY: 'auto' }}>
                  {Object.values(found).length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={16} sx={{ color: c.text }} />
                      <Typography sx={{ color: `${c.text}99`, fontSize: 13 }}>{remoteOnly || !isNativePlatform() ? 'The hub is listening for lamps waiting to be paired…' : 'Looking for lamps waiting to be paired…'}</Typography>
                    </Box>
                  )}
                  {Object.values(found).length > 1 && (
                    <Button
                      size="small"
                      onClick={() => setPicked(picked.length === Object.values(found).length ? [] : Object.keys(found))}
                      sx={{ color: c.text, mb: 0.5 }}
                    >
                      {picked.length === Object.values(found).length ? 'Clear all' : `Select all (${Object.values(found).length})`}
                    </Button>
                  )}
                  {Object.values(found).sort((a, b) => b.rssi - a.rssi).map((d) => (
                    <Box
                      key={d.deviceId}
                      onClick={() => togglePick(d.deviceId)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 0.5, mb: 0.5, borderRadius: '10px', cursor: 'pointer', bgcolor: picked.includes(d.deviceId) ? c.button : 'transparent', border: `1px solid ${c.text}33`,
                      }}
                    >
                      <Checkbox size="small" checked={picked.includes(d.deviceId)} sx={{ color: c.text, '&.Mui-checked': { color: c.text }, p: 0.5 }} />
                      <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: c.text, fontSize: 14, fontWeight: 600 }}>
                        {d.name || 'Mesh lamp'}
                        {(() => {
                          const known = lamps.find((l) => d.uuid && String(l.UniqueNumber || '').toUpperCase() === `BLM-${d.uuid.toUpperCase()}`);
                          return known ? ` — already "${known.Name}", pairing again renews it` : '';
                        })()}
                      </Typography>
                      <Typography sx={{ color: `${c.text}99`, fontSize: 11, fontFamily: 'monospace' }}>
                        {`${d.deviceId} · signal ${d.rssi}${d.uuid ? ` · ${d.uuid.slice(0, 8)}…` : ''}`}
                      </Typography>
                      </Box>
                    </Box>
                  ))}
                  {Object.values(found).length > 0 && (
                    <Button size="small" onClick={startMeshScan} sx={{ color: c.text, mt: 0.5 }}>Scan again</Button>
                  )}
                </Box>
              )}
              {ctl.busy && ctl.progress && (
                <Typography sx={{ color: c.text, fontSize: 13, mb: 1 }}>
                  {`${ctl.progress.verb || 'Pairing'} ${ctl.progress.i}/${ctl.progress.n} — ${ctl.progress.name}…`}
                </Typography>
              )}
              {ctl.busy && (
                <Box sx={{ mb: 2, p: 1, borderRadius: '8px', bgcolor: '#0b1020', maxHeight: 180, overflowY: 'auto' }}>
                  {ctl.meshLog.map((l, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Typography key={i} sx={{ color: '#d1fae5', fontSize: 11, fontFamily: 'monospace' }}>{l}</Typography>
                  ))}
                  {ctl.meshLog.length === 0 && <Typography sx={{ color: '#d1fae5', fontSize: 11, fontFamily: 'monospace' }}>Starting…</Typography>}
                </Box>
              )}
              {picked.length <= 1
                ? <TextField fullWidth label="Name" value={text} disabled={ctl.busy} onChange={(e) => setText(e.target.value)} placeholder="wwh1, wwh2 … (automatic if empty)" sx={{ mb: 2 }} />
                : <Typography sx={{ color: `${c.text}99`, fontSize: 12, mb: 2 }}>Several lamps are named automatically (wwh1, wwh2 …) — rename them afterwards.</Typography>}
              <TextField select fullWidth label="Room" value={pickRoom} disabled={ctl.busy} onChange={(e) => setPickRoom(e.target.value)} SelectProps={{ native: true }}>
                <option value="">Same as group</option>
                {rooms.map((r) => <option key={r.RoomID} value={String(r.RoomID)}>{r.RoomName}</option>)}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={ctl.busy} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={submitAddMesh} disabled={ctl.busy || picked.length === 0} variant="contained" sx={{ bgcolor: c.button, color: c.text }}>
                {(() => {
                  if (ctl.busy) return 'Pairing…';
                  return picked.length > 1 ? `Pair ${picked.length} lamps` : 'Pair';
                })()}
              </Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'add' && !ctl.isMesh && (
          <>
            <DialogTitle sx={{ color: c.text }}>Add a lamp</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14, mb: 2 }}>
                1. Every OTHER lamp must be switched off at the wall — a lamp in pairing mode accepts whatever it hears.
                {'\n'}2. Switch this lamp off at the wall, wait a moment, switch it back on.
                {'\n'}3. Within five seconds, tap Pair.
              </Typography>
              <TextField fullWidth label="Name" value={text} onChange={(e) => setText(e.target.value)} placeholder="wwh1, wwh2 … (automatic if empty)" sx={{ mb: 2 }} />
              <TextField select fullWidth label="Room" value={pickRoom} onChange={(e) => setPickRoom(e.target.value)} SelectProps={{ native: true }}>
                <option value="">Same as group</option>
                {rooms.map((r) => <option key={r.RoomID} value={String(r.RoomID)}>{r.RoomName}</option>)}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={ctl.busy} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={submitAdd} disabled={ctl.busy} variant="contained" sx={{ bgcolor: c.button, color: c.text }}>Pair</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'rename' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Rename</DialogTitle>
            <DialogContent><TextField autoFocus fullWidth value={text} onChange={(e) => setText(e.target.value)} /></DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button variant="contained" sx={{ bgcolor: c.button, color: c.text }} onClick={async () => { try { await ctl.rename(dialog.target, text); } catch (e) { show('Rename failed', e.message); } closeDialog(); }}>Save</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'move' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Move to room</DialogTitle>
            <DialogContent>
              <TextField select fullWidth value={pickRoom} onChange={(e) => setPickRoom(e.target.value)} SelectProps={{ native: true }}>
                {rooms.map((r) => <option key={r.RoomID} value={String(r.RoomID)}>{r.RoomName}</option>)}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button variant="contained" sx={{ bgcolor: c.button, color: c.text }} onClick={async () => { try { await ctl.moveToRoom(dialog.target, Number(pickRoom)); } catch (e) { show('Move failed', e.message); } closeDialog(); }}>Move</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'repair' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Pair again</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14 }}>Switch only this lamp off at the wall, switch it back on, then tap Send within five seconds.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={ctl.busy} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={async () => { await ctl.rePair(dialog.target); closeDialog(); }} disabled={ctl.busy} variant="contained" sx={{ bgcolor: c.button, color: c.text }}>Send</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'remove' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Remove {dialog.target.Name}?</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14 }}>
                {!isNativePlatform() && !(ctl.isMesh && ctl.hubUno) // eslint-disable-line no-nested-ternary
                  ? 'The lamp is removed from this app. There is no Bluetooth here, so the lamp itself is not reset — reset it at the wall switch before pairing it again.'
                  : ctl.isMesh
                    ? `The lamp is factory-reset ${isNativePlatform() ? 'over Bluetooth (stand near the lamps) or through the hub' : 'through the location’s hub'} and removed from this app. To use it again, add it as a new lamp.`
                    : 'The lamp is told to forget its identity and is removed from this app. To use it again, add it as a new lamp.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={async () => { const t = dialog.target; closeDialog(); await ctl.removeLamp(t); }} variant="contained" color="error">Remove</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'reverse' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Reverse white / warm white</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14, mb: 1.5 }}>
                Tick every lamp that shows WARM when you choose White (its LEDs are wired the other way round). The app then sends those lamps the opposite colour, so they match the rest.
                {ctl.isMesh ? ' Stand near the lamps when you save.' : ''}
              </Typography>
              {lamps.length > 1 && (
                <Button
                  size="small"
                  onClick={() => setReversedSel(reversedSel.length === lamps.length ? [] : lamps.map((l) => l.NodeId))}
                  sx={{ color: c.text, mb: 0.5 }}
                >
                  {reversedSel.length === lamps.length ? 'Clear all' : 'Select all'}
                </Button>
              )}
              {lamps.map((l) => {
                const on = reversedSel.includes(l.NodeId);
                return (
                  <Box
                    key={l.NodeId}
                    onClick={() => setReversedSel(on ? reversedSel.filter((x) => x !== l.NodeId) : [...reversedSel, l.NodeId])}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, p: 0.5, mb: 0.5, borderRadius: '10px', cursor: 'pointer', bgcolor: on ? c.button : 'transparent', border: `1px solid ${c.text}33`,
                    }}
                  >
                    <Checkbox size="small" checked={on} sx={{ color: c.text, '&.Mui-checked': { color: c.text }, p: 0.5 }} />
                    <Typography sx={{ color: c.text, fontSize: 14, fontWeight: 600 }}>{l.Name}</Typography>
                    {l.UniqueNumber && <Typography sx={{ color: `${c.text}66`, fontSize: 10, fontFamily: 'monospace', ml: 'auto' }}>{String(l.UniqueNumber).slice(-6)}</Typography>}
                  </Box>
                );
              })}
              {ctl.busy && ctl.progress && (
                <Typography sx={{ color: c.text, fontSize: 13, mt: 1 }}>{`${ctl.progress.verb} ${ctl.progress.i}/${ctl.progress.n} — ${ctl.progress.name}…`}</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={ctl.busy} sx={{ color: c.text }}>Cancel</Button>
              <Button
                variant="contained"
                disabled={ctl.busy}
                sx={{ bgcolor: c.button, color: c.text }}
                onClick={async () => {
                  const res = await ctl.setReversed(reversedSel);
                  closeDialog();
                  if (res && res.notUpdated.length) {
                    show('Saved — some lamps not updated', `These lamps could not be updated over Bluetooth: ${res.notUpdated.join(', ')}. Stand near them and use "Configure again" from each lamp's menu (owner).`);
                  }
                }}
              >
                {ctl.busy ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'removeAll' && (
          <>
            <DialogTitle sx={{ color: c.text }}>{`Remove all ${groupLamps.length} lamps from ${node.Name}?`}</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14 }}>
                {!isNativePlatform() && !(ctl.isMesh && ctl.hubUno) // eslint-disable-line no-nested-ternary
                  ? 'Each lamp is taken out of this group and removed from this app. There is no Bluetooth here, so the lamps themselves are not reset — reset them at the wall switch before pairing them again.'
                  : ctl.isMesh
                    ? `Each lamp is factory-reset ${isNativePlatform() ? 'over Bluetooth (stand near the lamps) or through the hub' : 'through the location’s hub'} — like new from the company — and taken out of this group. They stay known to the system, so any of them can be paired again later into any group, for any user.`
                    : 'Each lamp is told to forget its identity and is taken out of this group.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={submitRemoveAll} variant="contained" color="error">Remove all</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'detach' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Take {dialog.target.Name} out of {node.Name}?</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14 }}>The group keeps its lamps and stays in its room; it just stops following this parent&apos;s commands.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={async () => { const t = dialog.target; closeDialog(); await ctl.adoptGroup(t.NodeId, null); }} variant="contained" sx={{ bgcolor: c.button, color: c.text }}>Take out</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'removeGroup' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Delete {dialog.target.Name}?</DialogTitle>
            <DialogContent>
              <Typography sx={{ color: c.text, fontSize: 14 }}>The group has no lamps; it will leave its room and every share.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button onClick={async () => { const t = dialog.target; closeDialog(); await ctl.removeChildNode(t); }} variant="contained" color="error">Delete</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'adopt' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Add an existing group</DialogTitle>
            <DialogContent>
              {candidates.length === 0
                ? <Typography sx={{ color: c.text, fontSize: 14 }}>No other groups at this location.</Typography>
                : (
                  <TextField select fullWidth value={pickNode} onChange={(e) => setPickNode(e.target.value)} SelectProps={{ native: true }}>
                    {candidates.map((g) => <option key={g.NodeId} value={String(g.NodeId)}>{g.Name}{g.ParentNodeId ? ' (in another parent)' : ''}</option>)}
                  </TextField>
                )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} sx={{ color: c.text }}>Cancel</Button>
              <Button disabled={!pickNode} onClick={async () => { const id = Number(pickNode); closeDialog(); await ctl.adoptGroup(id, node.NodeId); }} variant="contained" sx={{ bgcolor: c.button, color: c.text }}>Add</Button>
            </DialogActions>
          </>
        )}

        {dialog?.kind === 'settings' && (
          <>
            <DialogTitle sx={{ color: c.text }}>Settings</DialogTitle>
            {ctl.isOwner && lamps.length > 0 && (
              <Box sx={{ px: 3, pb: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { setReversedSel(lamps.filter(isSwapped).map((l) => l.NodeId)); setDialog({ kind: 'reverse' }); }}
                  sx={{ color: c.text, borderColor: `${c.text}66`, justifyContent: 'flex-start' }}
                >
                  {`Reverse white / warm white… (${lamps.filter(isSwapped).length} of ${lamps.length})`}
                </Button>
              </Box>
            )}
            {ctl.isMesh ? (
              <DialogContent>
                <Typography sx={{ color: c.text, fontSize: 14, mb: 1 }}>Bluetooth Mesh (Telink)</Typography>
                <Typography sx={{ color: `${c.text}99`, fontSize: 12, mb: 1 }}>
                  Every lamp at this location is on one encrypted mesh network and passes commands on to the others, so the phone only has to reach one of them. A command to this {node.NodeType === 'parent' ? 'parent' : 'group'} is a single message to its group address.
                </Typography>
                <Typography sx={{ color: `${c.text}99`, fontSize: 12, fontFamily: 'monospace' }}>
                  {`Group address 0x${Number(node.MeshAddress || 0).toString(16).padStart(4, '0')}`}
                </Typography>
                <Typography sx={{ color: `${c.text}99`, fontSize: 12, fontFamily: 'monospace', mb: 2 }}>
                  {`${lamps.filter((l) => Number(l.MeshConfigured) === 1).length}/${lamps.length} lamps configured`}
                </Typography>
                <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>
                  {node.NodeType === 'parent' ? 'Parent' : 'Group'} · {node.UniqueNumber} · {lamps.length} lamps
                </Typography>
              </DialogContent>
            ) : (
            <DialogContent>
              <Typography sx={{ color: `${c.text}99`, fontSize: 12, mb: 1 }}>
                Encoding — change only if the lamps ignore commands. Applies to every lamp under this node; no re-pairing needed.
              </Typography>
              <TextField select fullWidth value={node.Codec || 'lampsmart_pro_v3'} disabled={locked && !ctl.isOwner} onChange={(e) => ctl.setCodec(e.target.value)} SelectProps={{ native: true }} sx={{ mb: 2 }}>
                {codecs.map((k) => <option key={k.id} value={k.id}>{k.app} — {k.label}{k.primary ? '' : ' (sub-series)'}</option>)}
              </TextField>

              <Typography sx={{ color: `${c.text}99`, fontSize: 12, mb: 1 }}>
                Air time per lamp (ms). Each packet is held this long so the lamp catches it; 300 is reliable. Lower it only if every lamp still reacts.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField size="small" type="number" value={holdText} onChange={(e) => setHoldText(e.target.value)} inputProps={{ min: 100, max: 3000, step: 50 }} sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={() => ctl.setHoldMs(Math.max(100, Math.min(3000, Number(holdText) || 300)))} sx={{ color: c.text, borderColor: `${c.text}66` }}>Save</Button>
              </Box>

              <Button fullWidth variant="outlined" disabled={locked && !ctl.isOwner} onClick={() => ctl.setIncludeFlags(Number(node.IncludeFlags ?? 1) !== 1)} sx={{ color: c.text, borderColor: `${c.text}66`, mb: 1 }}>
                Send Flags header: {Number(node.IncludeFlags ?? 1) === 1 ? 'ON' : 'OFF'}
              </Button>
              <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>Every lamp seen so far needs the header ON. Turn it off only if yours stops responding.</Typography>
              <Typography sx={{ color: `${c.text}99`, fontSize: 12, mt: 2 }}>
                {node.NodeType === 'parent' ? 'Parent' : 'Group'} · {node.UniqueNumber} · {lamps.length} lamps
              </Typography>
            </DialogContent>
            )}
            <DialogActions><Button onClick={closeDialog} sx={{ color: c.text }}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
